import { useEffect, useMemo, useState } from 'react';
import { motion as Motion } from 'motion/react';
import { X } from 'lucide-react';
import { colors, accent } from '@/lib/tokens';

type SpotlightAction = {
  label: string;
  onClick: () => void;
};

type Rect = { top: number; left: number; width: number; height: number };

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function OnboardingSpotlight({
  selector,
  title,
  body,
  stepLabel,
  primaryAction,
  secondaryAction,
  backAction,
  onSkip,
  noOverlay = false,
  highlightScale = 1,
}: {
  selector: string;
  title: string;
  body: string;
  stepLabel: string;
  primaryAction: SpotlightAction;
  secondaryAction?: SpotlightAction;
  backAction?: SpotlightAction;
  onSkip: () => void;
  noOverlay?: boolean;
  highlightScale?: number;
}) {
  const [rect, setRect] = useState<Rect | null>(null);

  useEffect(() => {
    const updateRect = () => {
      const target = document.querySelector(selector) as HTMLElement | null;
      if (!target) { setRect(null); return; }
      const next = target.getBoundingClientRect();
      setRect({ top: next.top, left: next.left, width: next.width, height: next.height });
    };

    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [selector]);

  const cardStyle = useMemo(() => {
    if (!rect) {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      } as const;
    }

    const margin = 16;
    const gap = 24;
    const cardWidth = Math.min(531, window.innerWidth - margin * 2);
    const cardHeight = 320;
    const maxLeft = window.innerWidth - cardWidth - margin;
    const maxTop = window.innerHeight - cardHeight - margin;
    const targetCenterX = rect.left + rect.width / 2;
    const targetCenterY = rect.top + rect.height / 2;
    const spaceAbove = rect.top - margin;
    const spaceBelow = window.innerHeight - (rect.top + rect.height) - margin;
    const spaceLeft = rect.left - margin;
    const spaceRight = window.innerWidth - (rect.left + rect.width) - margin;

    const placements = [
      {
        side: 'bottom',
        fits: spaceBelow >= cardHeight + gap,
        score: spaceBelow,
        top: rect.top + rect.height + gap,
        left: clamp(targetCenterX - cardWidth / 2, margin, maxLeft),
      },
      {
        side: 'top',
        fits: spaceAbove >= cardHeight + gap,
        score: spaceAbove,
        top: rect.top - cardHeight - gap,
        left: clamp(targetCenterX - cardWidth / 2, margin, maxLeft),
      },
      {
        side: 'right',
        fits: spaceRight >= cardWidth + gap,
        score: spaceRight,
        top: clamp(targetCenterY - cardHeight / 2, margin, maxTop),
        left: rect.left + rect.width + gap,
      },
      {
        side: 'left',
        fits: spaceLeft >= cardWidth + gap,
        score: spaceLeft,
        top: clamp(targetCenterY - cardHeight / 2, margin, maxTop),
        left: rect.left - cardWidth - gap,
      },
    ];

    const preferred =
      placements.find((placement) => placement.side === 'bottom' && placement.fits) ??
      placements.find((placement) => placement.side === 'top' && placement.fits) ??
      placements.find((placement) => placement.side === 'right' && placement.fits) ??
      placements.find((placement) => placement.side === 'left' && placement.fits) ??
      placements.sort((a, b) => b.score - a.score)[0];

    return {
      top: clamp(preferred.top, margin, maxTop),
      left: clamp(preferred.left, margin, maxLeft),
    };
  }, [rect]);

  const card = (
    <>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="mb-3 text-xs uppercase tracking-[0.18em]" style={{ color: accent }}>{stepLabel}</p>
          <h3 className="text-xl font-semibold" style={{ color: colors.textPrimary }}>{title}</h3>
        </div>
        <button
          type="button"
          onClick={onSkip}
          className="rounded-md p-2.5 transition-colors flex-shrink-0"
          style={{ color: colors.textMuted, background: colors.bgSubtle }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = colors.borderSubtle; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = colors.bgSubtle; }}
          aria-label="Skip tour"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <p className="mb-8 text-base leading-relaxed" style={{ color: colors.textSecondary }}>
        {body}
      </p>

      <div className="flex items-center justify-between gap-3">
        <div>
          {backAction && (
            <button
              type="button"
              onClick={backAction.onClick}
              className="rounded-md px-5 py-2.5 text-sm font-medium transition-all cursor-pointer"
              style={{ background: colors.bgSubtle, border: `1px solid ${colors.borderDefault}`, color: colors.textSecondary }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = colors.borderHover; (e.currentTarget as HTMLButtonElement).style.color = colors.textPrimary; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = colors.borderDefault; (e.currentTarget as HTMLButtonElement).style.color = colors.textSecondary; }}
            >
              {backAction.label}
            </button>
          )}
        </div>
        <div className="flex gap-3">
          {secondaryAction && (
            <button
              type="button"
              onClick={secondaryAction.onClick}
              className="rounded-md px-5 py-2.5 text-sm font-medium transition-all cursor-pointer"
              style={{ background: colors.bgSubtle, border: `1px solid ${colors.borderDefault}`, color: colors.textSecondary }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = colors.borderHover; (e.currentTarget as HTMLButtonElement).style.color = colors.textPrimary; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = colors.borderDefault; (e.currentTarget as HTMLButtonElement).style.color = colors.textSecondary; }}
            >
              {secondaryAction.label}
            </button>
          )}
          <button
            type="button"
            onClick={primaryAction.onClick}
            className="rounded-md px-5 py-2.5 text-sm font-medium transition-all cursor-pointer"
            style={{ background: colors.accentSubtle, border: `1px solid ${colors.accentBorder}`, color: accent }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = colors.accentHover; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = colors.accentSubtle; }}
          >
            {primaryAction.label}
          </button>
        </div>
      </div>
    </>
  );

  // noOverlay mode: no backdrop, card pinned to bottom-right corner
  if (noOverlay) {
    return (
      <div className="pointer-events-none fixed inset-0 z-[80]">
        <Motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
          className="pointer-events-auto absolute w-[min(420px,calc(100vw-32px))] rounded-2xl p-8"
          style={{
            bottom: 24,
            right: 24,
            background: colors.bgBase,
            border: `1px solid ${colors.borderDefault}`,
            boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
          }}
        >
          {card}
        </Motion.div>
      </div>
    );
  }

  return (
    <div className="pointer-events-none fixed inset-0 z-[80]">
      {rect ? (
        <Motion.div
          layout
          className="absolute rounded-xl"
          style={{
            top: rect.top - 8,
            left: rect.left - 8,
            width: rect.width + 16,
            height: rect.height + 16,
            border: `1px solid rgba(255,255,255,0.35)`,
            background: 'rgba(255,255,255,0.08)',
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.60)',
            transform: highlightScale !== 1 ? `scale(${highlightScale})` : undefined,
            transformOrigin: 'center',
          }}
        />
      ) : null}

      <div className={rect ? 'contents' : 'absolute inset-0 flex items-center justify-center pointer-events-none'}>
        <Motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
          className={`pointer-events-auto w-[min(531px,calc(100vw-32px))] rounded-2xl p-8 ${rect ? 'absolute' : ''}`}
          style={{
            ...(rect ? cardStyle : {}),
            background: colors.bgBase,
            border: `1px solid ${colors.borderDefault}`,
            boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
          }}
        >
          {card}
        </Motion.div>
      </div>
    </div>
  );
}
